import {InitiatiefFaseEnum} from './enums&types.js';
import {FteBucket, ReeksNotitie, ReeksPunt, ReeksSerie, StatusKleur, ToegekendStatusEnum} from './dashboard.js';

// payload types of the Overall, Personeel, Initiatieven and Financien pages (design spec 6.2, 7.3, 7.5, 7.6, 7.7)

export const NVT: string = 'n.v.t.';

//////////////////////////////////// number formats ////////////////////////////////////

// 12345.6 -> '12.345,6' (Dutch separators)
export function formatGetal(n: number, decimals: number): string {
    return n.toLocaleString('en-US', {minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: true})
        .replace(/,/g, 'X')
        .replace(/[.]/g, ',')
        .replace(/X/g, '.');
}

// whole numbers without a decimal, others with one
export function formatFte(fte: number): string {
    const afgerond: number = Math.round(fte * 10) / 10 || 0; // -0 becomes 0
    return `${formatGetal(afgerond, Number.isInteger(afgerond) ? 0 : 1)} FTE`;
}

export function formatMedewerkers(medewerkers: number): string {
    return formatGetal(Math.round(medewerkers), 0);
}

export function formatMln(bedrag: number): string {
    const mln: number   = Math.round(Math.abs(bedrag) / 100000) / 10;
    const teken: string = bedrag < 0 && mln > 0 ? '-' : '';
    return `${teken}\u20ac ${formatGetal(mln, 1)} mln`;
}

// 0.1234 -> '+12,3%'
export function formatSignedPercentage(fractie: number | undefined): string {
    if (fractie === undefined) {
        return NVT;
    }
    const pct: number    = Math.round(Math.abs(fractie) * 1000) / 10;
    const teken: string  = fractie < 0 && pct > 0 ? '-' : '+';
    return `${teken}${formatGetal(pct, 1)}%`;
}

// 0.876 -> '88%'
export function formatPercentage(fractie: number | undefined): string {
    return fractie === undefined ? '-' : `${formatGetal(fractie * 100, 0)}%`;
}

export function statusTekst(kleur: StatusKleur): string {
    switch (kleur) {
        case StatusKleur.groen: {
            return 'On-track';
        }
        case StatusKleur.amber: {
            return 'Risico op vertraging';
        }
        case StatusKleur.rood: {
            return 'Off-track';
        }
        default: {
            return 'Onbekend';
        }
    }
}

//////////////////////////////////// shared matrix ////////////////////////////////////

export type StatusCel = {
    tekst: string;
    kleur: StatusKleur;
};

export type StatusRij = {
    label: string;
    detail?: boolean;       // an indented '\u21b3' row under the previous row
    totaal?: boolean;       // rendered bold on grey, never coloured
    cellen: StatusCel[];
};

export type StatusMatrix = {
    kolommen: string[];
    rijen: StatusRij[];
};

//////////////////////////////////// overall (7.7) ////////////////////////////////////

export type OverallKaart = {
    label: string;
    waarde: string;
};

export type StatusBalk = {
    label: string;
    waarde: number;
    target: number | undefined;
    ratio: number | undefined;
    kleur: StatusKleur;
};

export type OverallPage = {
    kaarten: OverallKaart[];
    balken: StatusBalk[];
    matrix: StatusMatrix;
};

export const MANAGEMENT_RIJEN: string[] = [
    'Maximale formatie (FTE)',
    'Cumulatieve, geidentificeerde impact van initiatieven (FTE)',
    'Cumulatieve, gemeten impact van initiatieven (FTE)',
    'Toegekend IZA-budget (EUR)',
    'Gerealiseerde kosten (EUR)',
    'Geprognotiseerde kosten eerstvolgende Q (EUR)',
    'Wachttijden binnen de treeknorm (%)',
    'Adherentie (%)',
    'Huisartsverwijzingen MSZ (aantal)',
    'Huisartsverwijzingen GGZ (aantal)',
    'Volume unieke patienten / clienten (aantal)',
    'Productiviteit (volume per FTE)',
];

//////////////////////////////////// personeel (7.6) ////////////////////////////////////

export type PersoneelVariant = 'samenvatting' | 'geidentificeerd' | 'gemeten';

export const PERSONEEL_VARIANTEN: PersoneelVariant[] = ['samenvatting', 'geidentificeerd', 'gemeten'];

export function PersoneelVariant_name(v: PersoneelVariant): string {
    switch (v) {
        case 'samenvatting': {
            return 'Samenvatting';
        }
        case 'geidentificeerd': {
            return 'Geidentificeerde impact per organisatie';
        }
        case 'gemeten': {
            return 'Gemeten impact per organisatie';
        }
    }
}

export type ImpactPerOrganisatie = {
    organisatie: string;
    welInUitvoering: number;
    nietInUitvoering: number;
    welMedewerkers: number;         // welInUitvoering expressed in medewerkers (7.6)
    nietMedewerkers: number;
    gemeten: number;
    targetQ: number | undefined;
    targetQ1: number | undefined;
    voortgang: number | undefined;  // geidentificeerd / targetQ
    kleur: StatusKleur;
};

export type MaxFormatieTrend = {
    organisatie: string;
    serie: ReeksSerie;              // the maxFormatie points up to Q, target = vasteWaarde at Q+1 when known
    eersteMeting: ReeksPunt | undefined;
    targetQ1: number | undefined;
    kleur: StatusKleur;             // inverted ratio of the last point against the Q+1 target
    notities: ReeksNotitie[];
};

export type PersoneelPage = {
    matrix: StatusMatrix;
    impact: ImpactPerOrganisatie[];
    impactTotaal: ImpactPerOrganisatie;
    trends: MaxFormatieTrend[];
};

//////////////////////////////////// initiatieven (7.5) ////////////////////////////////////

export type DeltaKaart = {
    label: string;
    waarde: number;
    delta: number | undefined;
};

export type FaseVerdeling = {
    fase: InitiatiefFaseEnum;
    aantal: number;
    fte: number;                            // sum of the identified impact
    percentageVanTarget: number | undefined; // fte / target geidentificeerd, undefined without a target
};

export type FaseStatistiek = {
    fase: InitiatiefFaseEnum;
    waarden: number[];                      // verblijftijd of every initiative now in this phase, ZZC-wide
    mediaan: number;
    gemiddelde: number;
    q1: number;
    q3: number;
    grens: number;                          // Tukey fence
    organisatieMediaan: number | undefined; // when exactly one organisation group is chosen
};

export type LangsteRij = {
    initiatief: string;
    organisatie: string;
    fase: InitiatiefFaseEnum;
    faseIngang: string;
    kwartalenInFase: number;
    fte: number;
};

export type TijdlijnCel = {
    kwartaal: string;
    fase: InitiatiefFaseEnum;
    onderbroken: boolean;                   // forward-filled, no snapshot in this quarter
};

export type TijdlijnRij = {
    id: string;
    label: string;
    organisatie: string;
    leeftijd: number;
    kwartalenInHuidigeFase: number;
    fte: number;
    teruggezet: boolean;
    onderbroken: boolean;
    cellen: TijdlijnCel[];
};

export type InitiatiefDetail = {
    id: string;
    organisatie: string;
    initiatief: string;
    fase: InitiatiefFaseEnum;
    faseIngang: string;
    kwartalenInFase: number;
    fte: number;
    bucket: FteBucket;
    uitschieter: boolean;
    toelichting: string;
};

export type InitiatievenPage = {
    kaarten: DeltaKaart[];
    verdeling: FaseVerdeling[];
    statistiek: FaseStatistiek[];
    snapshots: number;                      // distinct quarters with a snapshot, over every organisation
    langste: LangsteRij[];
    tijdlijn: TijdlijnRij[];
    details: InitiatiefDetail[];
    kwartalen: string[];                    // the timeline columns, oldest first
};

// the phases that get a box plot, an outlier fence and a place in the top 10
export const STRIP_FASEN: InitiatiefFaseEnum[] = [
    InitiatiefFaseEnum.initiatief,
    InitiatiefFaseEnum.orientatie,
    InitiatiefFaseEnum.realisatie,
    InitiatiefFaseEnum.afronding,
];

export const FASE_KLEUR: Record<InitiatiefFaseEnum, string> = {
    initiatief: '#dbeafe',
    orientatie: '#93c5fd',
    realisatie: '#2b4371',
    afronding : '#a4c130',
    afgerond  : '#6aa84f',
    onbekend  : '#bfbfbf',
};

//////////////////////////////////// financien (7.3, 7.9) ////////////////////////////////////

export type FinanceThema = {
    label: string;
    themaNaam: string;      // the CabTheme name of the report items
};

export const FINANCE_THEMES: FinanceThema[] = [
    {label: 'Capability',     themaNaam: 'Transformatie Capability Ondersteuning'},
    {label: 'Communicatie',   themaNaam: 'Communicatie'},
    {label: 'Digitalisering', themaNaam: 'Digitalisering'},
    {label: 'F&C',            themaNaam: 'Financien en Control'},
    {label: 'Participatie',   themaNaam: 'Inwonerparticipatie'},
    {label: 'ZDC',            themaNaam: 'Datacoalitie'},
];

export const KOSTENPOST_VOLGORDE: string[] = ['Personeel', 'ICT & Software', 'Opleiding & Communicatie', 'Programmabegeleiding', 'Huisvesting & Inboedel'];

export const OO_LABEL: string           = 'OO';
export const OO_DETAIL_LABELS: string[] = ['Capability', 'Communicatie', 'F&C', 'Participatie', 'ZDC'];
export const OO_PIVOT_LABELS: string[]  = ['Capability', 'Communicatie', 'F&C', 'ZDC'];
export const PIVOT_KOLOMMEN: string[]   = ['ADRZ', 'Digitalisering', 'Emergis', 'Huisartsen', 'Kerngezond', OO_LABEL, 'SVRZ', 'Zorg In Balans', 'ZorgSaam'];
export const TOTAAL_LABEL: string       = 'Totaal';

export type FinancienSamenvattingRij = {
    label: string;
    waarde: number;
    target: number | undefined;
    voortgang: number | undefined;  // waarde / target - 1
    kleur: StatusKleur;
    definitie: string;
};

export type UitgavenRij = {
    kostenpost: string;
    beschikbaar: number;
    gerealiseerd: number;
    prognose: number;
    resterend: number;
    uitputting: number | undefined;
    kleur: StatusKleur;
};

export type UitgavenOverzicht = {
    beschikbaar: number;
    gerealiseerd: number;
    prognose: number;
    resterend: number;
    uitputting: number | undefined;
    rijen: UitgavenRij[];           // Totaal first, then the kostenposten in KOSTENPOST_VOLGORDE, then unknown ones
};

export type WaterfallStap = {
    status: ToegekendStatusEnum;
    bedrag: number;
};

export type Waterfall = {
    stappen: WaterfallStap[];
    totaal: number;
    beschikbaar: number;
};

export type ThemaKolom = {
    label: string;
    beschikbaar: number;
    prognose: number;               // realisatie plus prognose
    verschil: number;
    percentage: number | undefined; // prognose / beschikbaar
};

export type ThemaMatrix = {
    kolommen: ThemaKolom[];         // PIVOT_KOLOMMEN order, other labels after them, Totaal last
    ooKolommen: ThemaKolom[];       // the OO breakdown, OO_DETAIL_LABELS order
    volgendKwartaal: string;
};

export type PivotRij = {
    label: string;                  // a ToegekendStatusEnum or TOTAAL_LABEL
    waarden: number[];              // millions of euros per column
    totaal: number;
};

export type Pivot = {
    kolommen: string[];
    rijen: PivotRij[];
};

export type FinancienPage = {
    samenvatting: FinancienSamenvattingRij[];
    uitgaven: UitgavenOverzicht;
    waterfall: Waterfall;
    themaMatrix: ThemaMatrix;
    pivotOrganisatie: Pivot;
    pivotThema: Pivot;
};
